import {
  Handshake,
  Scale,
  Megaphone,
  CalendarRange,
  TrendingUp,
  Network,
  Disc3,
  ArrowUpRight,
} from "lucide-react";
import { useReveal } from "../lib/useReveal";

const services = [
  {
    icon: Handshake,
    title: "Brand Deals",
    description:
      "Sourcing, vetting, and closing brand partnerships that fit the creator — not just the rate card.",
    span: "md:col-span-8",
    height: "min-h-[260px] md:min-h-[300px]",
    accent: true,
  },
  {
    icon: Scale,
    title: "Deal Strategy",
    description:
      "We push for usage, exclusivity, and long-term value on every contract.",
    span: "md:col-span-4",
    height: "min-h-[260px] md:min-h-[300px]",
  },
  {
    icon: Disc3,
    title: "Song Promotion",
    description:
      "A core part of what we do — building rollouts, sounds, and creator-driven campaigns that take songs viral across TikTok, Reels, and beyond.",
    span: "md:col-span-8",
    height: "min-h-[280px]",
    accent: true,
  },
  {
    icon: Megaphone,
    title: "Social Media Strategy",
    description:
      "Format, cadence, and platform-specific playbooks that compound over time.",
    span: "md:col-span-4",
    height: "min-h-[280px]",
  },
  {
    icon: CalendarRange,
    title: "Campaign Management",
    description:
      "From kickoff to wrap report — creative direction, deliverables, and timelines handled.",
    span: "md:col-span-4",
    height: "min-h-[240px]",
  },
  {
    icon: TrendingUp,
    title: "Creator Growth",
    description:
      "Audience strategy, virality systems, and long-form to short-form pipelines.",
    span: "md:col-span-4",
    height: "min-h-[240px]",
  },
  {
    icon: Network,
    title: "Partnership Outreach",
    description:
      "Direct lines into brands, labels, and platforms — built over years of relationships.",
    span: "md:col-span-4",
    height: "min-h-[240px]",
  },
];

export const Services = () => {
  const headRef = useReveal();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#050505] py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={headRef} className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <span className="text-xs uppercase tracking-[0.28em] text-neutral-500 font-medium">
              02 / Services
            </span>
            <h2
              data-testid="services-heading"
              className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.02]"
            >
              What we do for
              <br />
              <span className="italic font-medium">our creators.</span>
            </h2>
          </div>
          <p className="max-w-md text-base md:text-lg text-neutral-400 leading-relaxed">
            A full-stack management offering — built around creators who live online
            and operate at internet speed.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <ServiceCard
                key={s.title}
                index={i}
                Icon={Icon}
                title={s.title}
                description={s.description}
                span={s.span}
                height={s.height}
                accent={s.accent}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ index, Icon, title, description, span, height, accent }) => {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
      style={{ transitionDelay: `${index * 70}ms` }}
      className={`reveal group relative col-span-1 ${span} ${height} rounded-[20px] border border-white/10 bg-[#0c0c0c] hover:border-white/30 hover:-translate-y-1.5 transition-all duration-500 p-7 md:p-9 flex flex-col justify-between overflow-hidden border-shine`}
    >
      {accent && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-white/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        />
      )}

      <div className="flex items-start justify-between">
        <div className="h-11 w-11 rounded-full border border-white/15 bg-white/5 flex items-center justify-center">
          <Icon className="h-5 w-5 text-white" strokeWidth={1.5} />
        </div>
        <ArrowUpRight
          className="h-5 w-5 text-neutral-500 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
          strokeWidth={1.5}
        />
      </div>

      <div className="mt-10">
        <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-white">
          {title}
        </h3>
        <p className="mt-3 text-sm md:text-base text-neutral-400 leading-relaxed max-w-md">
          {description}
        </p>
      </div>
    </div>
  );
};

export default Services;
